import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  UserPlus, Upload, FileText, CheckCircle, AlertCircle, 
  Building2, Briefcase, ArrowRight, ArrowLeft, Loader2, Shield
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

// Service categories for vendors
const SERVICE_CATEGORIES = [
  { id: "design", name: "Design", description: "Event design, booth design, branding" },
  { id: "production", name: "Production", description: "Event production, staging, AV" },
  { id: "digital_services", name: "Digital Services", description: "Digital marketing, web development" },
  { id: "brand_activations", name: "Brand Activations", description: "Brand activation events, experiential marketing" },
  { id: "brand_reminders", name: "Brand Reminders", description: "Branded promotional items and gifts" },
  { id: "medical_services", name: "Medical Services", description: "Medical education, advisory boards, KOL management" },
  { id: "heor", name: "HEOR", description: "Health Economics and Outcomes Research services" },
  { id: "screens", name: "Screens & LED", description: "LED screens, video walls, displays" },
  { id: "printing", name: "Printing", description: "Print materials, signage, banners" },
  { id: "social_media", name: "Social Media Campaigns", description: "Social media marketing, content creation" },
  { id: "accommodation_travel", name: "Accommodation & Tickets", description: "Hotels, flights, travel arrangements" },
  { id: "catering", name: "Catering", description: "Food and beverage services" },
  { id: "photography", name: "Photography/Videography", description: "Event photography, video production" },
  { id: "event_staffing", name: "Event Staffing", description: "Hostesses, promoters, event staff" }
];

const POSITIONING_OPTIONS = [
  { id: "premium_quality", name: "Premium Quality", description: "High-end execution, top-tier creative, premium pricing", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { id: "cost_efficient", name: "Cost Efficient", description: "Competitive pricing without compromising quality", color: "bg-green-50 text-green-700 border-green-200" },
  { id: "speed_agility", name: "Speed & Agility", description: "Fast turnaround, tight deadline specialist", color: "bg-blue-50 text-blue-700 border-blue-200" },
  { id: "niche_specialist", name: "Niche Specialist", description: "Deep expertise in a specific service area", color: "bg-purple-50 text-purple-700 border-purple-200" },
  { id: "innovation_focused", name: "Innovation Focused", description: "Cutting-edge technology, digital-first approach", color: "bg-cyan-50 text-cyan-700 border-cyan-200" }
];

// Document requirements
const REQUIRED_DOCUMENTS = [
  { id: "cr", name: "Commercial Registration (CR)", description: "Valid trade license or commercial registration" },
  { id: "vat_certificate", name: "VAT Certificate", description: "VAT registration certificate" },
  { id: "bank_letter", name: "Bank Letter", description: "Bank account verification letter" },
  { id: "national_address", name: "National Address", description: "Official national address certificate" },
  { id: "company_profile", name: "Company Profile", description: "Company profile or brochure" },
  { id: "other_documents", name: "Other Documents", description: "Questionnaire, certifications, or additional supporting documents", optional: true }
];

// Countries list
const COUNTRIES = [
  { code: "SA", name: "Saudi Arabia" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "EG", name: "Egypt" },
  { code: "JO", name: "Jordan" },
  { code: "KW", name: "Kuwait" },
  { code: "BH", name: "Bahrain" },
  { code: "QA", name: "Qatar" },
  { code: "OM", name: "Oman" },
  { code: "LB", name: "Lebanon" },
  { code: "IQ", name: "Iraq" },
  { code: "MA", name: "Morocco" },
  { code: "TN", name: "Tunisia" },
  { code: "DZ", name: "Algeria" },
  { code: "PK", name: "Pakistan" },
  { code: "TR", name: "Turkey" },
  { code: "UK", name: "United Kingdom" },
  { code: "US", name: "United States" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "IN", name: "India" },
  { code: "OTHER", name: "Other" }
];

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  
  // Get initial user type from URL params
  const initialType = searchParams.get('type') || 'brand_manager';
  
  // Multi-step form state
  const [currentStep, setCurrentStep] = useState(1);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirm_password: '',
    full_name: '',
    phone: '',
    user_type: initialType,
    company_name: '',
    company_type: initialType === 'vendor' ? 'vendor' : 'pharma',
    is_multinational: false,
    description: '',
    services: [],
    // New vendor fields
    business_email: '',
    contact_person_name: '',
    contact_person_mobile: '',
    contact_person_email: '',
    operating_countries: [],
    positioning: ''
  });

  // Calculate isVendor and totalSteps based on CURRENT formData.user_type (not initialType)
  const isVendor = formData.user_type === 'vendor';
  const totalSteps = isVendor ? 3 : 2;
  
  // Document upload state
  const [documents, setDocuments] = useState({
    cr: null,
    vat_certificate: null,
    bank_letter: null,
    national_address: null,
    company_profile: null,
    other_documents: null
  });
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedDocs, setUploadedDocs] = useState({});
  
  const [loading, setLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  
  // T&C state
  const [tcAccepted, setTcAccepted] = useState(false);
  const [tcTerms, setTcTerms] = useState(null);
  const [tcLoading, setTcLoading] = useState(false);
  const [showTcModal, setShowTcModal] = useState(false);

  // Fetch T&C when user type changes or services change
  useEffect(() => {
    const fetchTC = async () => {
      setTcLoading(true);
      try {
        const tcType = formData.user_type === 'vendor' ? 'vendor' : 'customer';
        const category = formData.user_type === 'vendor' && formData.services.length > 0
          ? formData.services[0] : '';
        const res = await axios.get(
          `${API}/api/marketplace/tc/active?type=${tcType}${category ? `&category=${category}` : ''}`
        );
        setTcTerms(res.data);
        setTcAccepted(false);
      } catch {
        setTcTerms(null);
      } finally {
        setTcLoading(false);
      }
    };
    fetchTC();
  }, [formData.user_type, formData.services]);

  const handleServiceToggle = (serviceId) => {
    const currentServices = formData.services;
    const isSelected = currentServices.includes(serviceId);
    
    if (isSelected) {
      setFormData(prev => ({
        ...prev,
        services: prev.services.filter(s => s !== serviceId)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        services: [...prev.services, serviceId]
      }));
    }
  };

  const handleCountryToggle = (countryCode) => {
    const currentCountries = formData.operating_countries;
    const isSelected = currentCountries.includes(countryCode);
    
    if (isSelected) {
      setFormData(prev => ({
        ...prev,
        operating_countries: prev.operating_countries.filter(c => c !== countryCode)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        operating_countries: [...prev.operating_countries, countryCode]
      }));
    }
  };

  const handleFileChange = (docType, file) => {
    if (file && file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum 10MB allowed.');
      return;
    }
    setDocuments(prev => ({ ...prev, [docType]: file }));
  };

  const uploadDocument = async (docType, file, compId) => {
    const formDataUpload = new FormData();
    formDataUpload.append('company_id', compId);
    formDataUpload.append('document_type', docType);
    formDataUpload.append('file', file);

    try {
      setUploadProgress(prev => ({ ...prev, [docType]: 0 }));
      
      const response = await axios.post(
        `${API}/api/marketplace/auth/register/upload-document`,
        formDataUpload,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({ ...prev, [docType]: percent }));
          }
        }
      );
      
      setUploadedDocs(prev => ({ ...prev, [docType]: response.data }));
      setUploadProgress(prev => ({ ...prev, [docType]: 100 }));
      return true;
    } catch (error) {
      console.error(`Error uploading ${docType}:`, error);
      toast.error(`Failed to upload ${REQUIRED_DOCUMENTS.find(d => d.id === docType)?.name}`);
      setUploadProgress(prev => ({ ...prev, [docType]: -1 }));
      return false;
    }
  };

  const validateStep1 = () => {
    if (!formData.full_name || !formData.email || !formData.password || !formData.confirm_password) {
      toast.error('Please fill in all required fields');
      return false;
    }
    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return false;
    }
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.company_name) {
      toast.error('Company name is required');
      return false;
    }
    if (formData.user_type === 'vendor') {
      if (!formData.business_email) {
        toast.error('Business email is required');
        return false;
      }
      if (formData.services.length === 0) {
        toast.error('Please select at least one service category');
        return false;
      }
      if (!formData.contact_person_name || !formData.contact_person_mobile || !formData.contact_person_email) {
        toast.error('Contact person details are required');
        return false;
      }
      if (formData.operating_countries.length === 0) {
        toast.error('Please select at least one operating country');
        return false;
      }
      if (!formData.positioning) {
        toast.error('Please select your market positioning');
        return false;
      }
    }
    return true;
  };

  const validateStep3 = () => {
    const requiredDocs = REQUIRED_DOCUMENTS.filter(doc => !doc.optional);
    const missingDocs = requiredDocs.filter(doc => !documents[doc.id]);
    if (missingDocs.length > 0) {
      toast.error(`Please upload: ${missingDocs.map(d => d.name).join(', ')}`);
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    setCurrentStep(prev => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // For brand managers, just 2 steps (validate step 1 and 2)
    if (formData.user_type === 'brand_manager') {
      if (!validateStep1() || !validateStep2()) return;
    } else {
      // For vendors, must be on step 3 and validate all steps
      if (currentStep !== 3) {
        toast.error('Please complete all steps before submitting');
        return;
      }
      if (!validateStep1() || !validateStep2() || !validateStep3()) return;
    }

    setLoading(true);
    
    try {
      // Validate T&C acceptance
      if (tcTerms && !tcAccepted) {
        toast.error('You must accept the Terms & Conditions to register');
        setLoading(false);
        return;
      }

      // Step 1: Register user and company (include tc_terms_id if accepted)
      const registrationData = { ...formData };
      if (tcTerms && tcAccepted) {
        registrationData.tc_terms_id = tcTerms.id;
      }
      const result = await register(registrationData);
      
      if (!result.success) {
        toast.error(result.error);
        setLoading(false);
        return;
      }
      
      // If vendor, upload documents
      if (formData.user_type === 'vendor' && result.company_id) {
        setCompanyId(result.company_id);
        
        // Upload all documents
        const uploadPromises = REQUIRED_DOCUMENTS.map(doc => 
          documents[doc.id] ? uploadDocument(doc.id, documents[doc.id], result.company_id) : Promise.resolve(true)
        );
        
        const uploadResults = await Promise.all(uploadPromises);
        const allUploaded = uploadResults.every(r => r);
        
        if (!allUploaded) {
          toast.warning('Some documents failed to upload. You can upload them later from your profile.');
        }
      }
      
      setRegistrationComplete(true);
      toast.success('Registration successful!');
      
      // Redirect after 3 seconds
      setTimeout(() => {
        navigate('/marketplace/login');
      }, 3000);
      
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Success screen
  if (registrationComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-orange-50 py-12 px-4 flex items-center justify-center">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Registration Successful!</h2>
            <p className="text-slate-600 mb-6">
              {formData.user_type === 'vendor' 
                ? 'Your documents are being reviewed. You will be notified once your account is approved.'
                : 'Your account is pending admin approval. You will be notified once approved.'}
            </p>
            <Button onClick={() => navigate('/marketplace/login')} className="bg-blue-600 hover:bg-blue-700">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-orange-50 py-12 px-4">
      <Card className="max-w-3xl mx-auto">
        <CardHeader className="text-center">
          <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-orange-500 bg-clip-text text-transparent mb-2">
            Brand Nexus
          </div>
          <CardTitle className="text-2xl">Create Your Account</CardTitle>
          <CardDescription>Join the pharmaceutical B2B marketplace</CardDescription>
          
          {/* Progress indicator */}
          <div className="mt-6">
            <div className="flex justify-between mb-2">
              {/* Step 1 */}
              <div className="flex items-center flex-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep > 1 ? 'bg-green-500 text-white' :
                  currentStep === 1 ? 'bg-blue-600 text-white' :
                  'bg-slate-200 text-slate-500'
                }`}>
                  {currentStep > 1 ? <CheckCircle className="w-5 h-5" /> : '1'}
                </div>
                <div className={`flex-1 h-1 mx-2 ${currentStep > 1 ? 'bg-green-500' : 'bg-slate-200'}`} />
              </div>
              {/* Step 2 */}
              <div className={`flex items-center ${isVendor ? 'flex-1' : ''}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep > 2 ? 'bg-green-500 text-white' :
                  currentStep === 2 ? 'bg-blue-600 text-white' :
                  'bg-slate-200 text-slate-500'
                }`}>
                  {currentStep > 2 ? <CheckCircle className="w-5 h-5" /> : '2'}
                </div>
                {isVendor && <div className={`flex-1 h-1 mx-2 ${currentStep > 2 ? 'bg-green-500' : 'bg-slate-200'}`} />}
              </div>
              {/* Step 3 (vendor only) */}
              {isVendor && (
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep === 3 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'
                  }`}>
                    3
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-between text-xs text-slate-500">
              <span>User Info</span>
              <span>Company</span>
              {isVendor && <span>Documents</span>}
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Step 1: User Information */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-lg text-slate-900 flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-blue-600" />
                  User Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      data-testid="fullname-input"
                      required
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      data-testid="phone-input"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      data-testid="email-input"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="user_type">Account Type *</Label>
                    <Select
                      value={formData.user_type}
                      onValueChange={(value) => {
                        setFormData({ 
                          ...formData, 
                          user_type: value,
                          company_type: value === 'brand_manager' ? 'pharma' : 'vendor',
                          services: []
                        });
                      }}
                    >
                      <SelectTrigger data-testid="usertype-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="brand_manager">Brand Manager (Pharma)</SelectItem>
                        <SelectItem value="vendor">Vendor/Agency</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="password">Password *</Label>
                    <Input
                      id="password"
                      data-testid="password-input"
                      type="password"
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    />
                    <p className="text-xs text-slate-500 mt-1">Minimum 8 characters</p>
                  </div>
                  
                  <div>
                    <Label htmlFor="confirm_password">Confirm Password *</Label>
                    <Input
                      id="confirm_password"
                      data-testid="confirm-password-input"
                      type="password"
                      required
                      value={formData.confirm_password}
                      onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Company Information */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-lg text-slate-900 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  Company Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="company_name">Company Name *</Label>
                    <Input
                      id="company_name"
                      data-testid="company-name-input"
                      required
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    />
                  </div>
                  
                  {/* Business Email - mandatory for vendors */}
                  {formData.user_type === 'vendor' && (
                    <div>
                      <Label htmlFor="business_email">Business Email *</Label>
                      <Input
                        id="business_email"
                        data-testid="business-email-input"
                        type="email"
                        required
                        value={formData.business_email}
                        onChange={(e) => setFormData({ ...formData, business_email: e.target.value })}
                        placeholder="info@company.com"
                      />
                    </div>
                  )}
                </div>

                {/* Vendor Positioning */}
                {formData.user_type === 'vendor' && (
                  <div>
                    <Label htmlFor="positioning">Market Positioning *</Label>
                    <p className="text-sm text-slate-500 mb-2">How do you want to position your company in the marketplace?</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {POSITIONING_OPTIONS.map((option) => {
                        const isSelected = formData.positioning === option.id;
                        return (
                          <div
                            key={option.id}
                            onClick={() => setFormData({ ...formData, positioning: option.id })}
                            data-testid={`positioning-${option.id}`}
                            className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${
                              isSelected
                                ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                                : 'border-slate-200 hover:border-blue-300'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                                isSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-300'
                              }`}>
                                {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{option.name}</p>
                                <p className="text-xs text-slate-500">{option.description}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="is_multinational">Multinational Company</Label>
                    <p className="text-sm text-slate-500">Operating in multiple countries</p>
                  </div>
                  <Switch
                    id="is_multinational"
                    checked={formData.is_multinational}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_multinational: checked })}
                  />
                </div>

                <div>
                  <Label htmlFor="description">Company Description</Label>
                  <Textarea
                    id="description"
                    data-testid="description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    placeholder="Brief description of your company..."
                  />
                </div>

                {/* Contact Person Details (Vendors only) */}
                {formData.user_type === 'vendor' && (
                  <div className="pt-4 border-t">
                    <Label className="text-base font-semibold mb-3 block">Contact Person Details *</Label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="contact_person_name">Contact Name *</Label>
                        <Input
                          id="contact_person_name"
                          data-testid="contact-name-input"
                          value={formData.contact_person_name}
                          onChange={(e) => setFormData({ ...formData, contact_person_name: e.target.value })}
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_person_mobile">Mobile Phone *</Label>
                        <Input
                          id="contact_person_mobile"
                          data-testid="contact-mobile-input"
                          type="tel"
                          value={formData.contact_person_mobile}
                          onChange={(e) => setFormData({ ...formData, contact_person_mobile: e.target.value })}
                          placeholder="+966 5XXXXXXXX"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_person_email">Contact Email *</Label>
                        <Input
                          id="contact_person_email"
                          data-testid="contact-email-input"
                          type="email"
                          value={formData.contact_person_email}
                          onChange={(e) => setFormData({ ...formData, contact_person_email: e.target.value })}
                          placeholder="contact@company.com"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Operating Countries (Vendors only) */}
                {formData.user_type === 'vendor' && (
                  <div className="pt-4 border-t">
                    <Label className="text-base font-semibold mb-2 block">Countries of Operation *</Label>
                    <p className="text-sm text-slate-500 mb-3">Select all countries where your company operates</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {COUNTRIES.map((country) => {
                        const isSelected = formData.operating_countries.includes(country.code);
                        return (
                          <div
                            key={country.code}
                            onClick={() => handleCountryToggle(country.code)}
                            className={`border rounded-lg p-2 cursor-pointer transition-all text-sm ${
                              isSelected
                                ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                                : 'border-slate-200 hover:border-blue-300'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                                isSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-300'
                              }`}>
                                {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                              </div>
                              <span className={isSelected ? 'font-medium text-blue-700' : 'text-slate-700'}>
                                {country.name}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {formData.operating_countries.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="text-sm text-slate-500">Selected:</span>
                        {formData.operating_countries.map(code => {
                          const country = COUNTRIES.find(c => c.code === code);
                          return (
                            <Badge key={code} variant="secondary" className="bg-green-100 text-green-700">
                              {country?.name}
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Service Categories (Vendors only) */}
                {formData.user_type === 'vendor' && (
                  <div className="pt-4 border-t">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-blue-600" />
                      Services Offered *
                    </Label>
                    <p className="text-sm text-slate-500 mb-4">Select at least one service category</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {SERVICE_CATEGORIES.map((service) => {
                        const isSelected = formData.services.includes(service.id);
                        return (
                          <div
                            key={service.id}
                            onClick={() => handleServiceToggle(service.id)}
                            className={`border rounded-lg p-3 cursor-pointer transition-all ${
                              isSelected
                                ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                                : 'border-slate-200 hover:border-blue-300'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                                isSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-300'
                              }`}>
                                {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{service.name}</p>
                                <p className="text-xs text-slate-500">{service.description}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {formData.services.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="text-sm text-slate-500">Selected:</span>
                        {formData.services.map(serviceId => {
                          const service = SERVICE_CATEGORIES.find(s => s.id === serviceId);
                          return (
                            <Badge key={serviceId} variant="secondary" className="bg-blue-100 text-blue-700">
                              {service?.name}
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Document Upload (Vendors only) */}
            {currentStep === 3 && formData.user_type === 'vendor' && (
              <div className="space-y-4">
                <h3 className="font-semibold text-lg text-slate-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Verification Documents
                </h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <span className="font-medium">Accepted formats:</span> PDF, DOC, DOCX, JPG, PNG
                    <span className="mx-2">•</span>
                    <span className="font-medium">Max size:</span> 10MB per file
                  </p>
                </div>
                
                <div className="space-y-4">
                  {REQUIRED_DOCUMENTS.map((doc) => (
                    <div key={doc.id} className={`border rounded-lg p-4 ${doc.optional ? 'border-dashed' : ''}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <Label className="text-base">
                            {doc.name} {!doc.optional && '*'}
                            {doc.optional && <span className="text-slate-400 font-normal ml-1">(Optional)</span>}
                          </Label>
                          <p className="text-xs text-slate-500">{doc.description}</p>
                          <p className="text-xs text-blue-600 mt-1">PDF, DOC, DOCX, JPG, PNG</p>
                        </div>
                        {documents[doc.id] && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Selected
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Input
                          type="file"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileChange(doc.id, e.target.files[0])}
                          className="flex-1"
                        />
                      </div>
                      
                      {documents[doc.id] && (
                        <p className="text-xs text-green-600 mt-2">
                          ✓ {documents[doc.id].name} ({(documents[doc.id].size / 1024).toFixed(1)} KB)
                        </p>
                      )}
                      
                      {uploadProgress[doc.id] !== undefined && uploadProgress[doc.id] >= 0 && uploadProgress[doc.id] < 100 && (
                        <div className="mt-2">
                          <Progress value={uploadProgress[doc.id]} className="h-2" />
                          <p className="text-xs text-slate-500 mt-1">Uploading... {uploadProgress[doc.id]}%</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Important</p>
                      <p className="text-sm text-amber-700">
                        Your documents will be reviewed by our admin team. You'll be notified once your account is verified and activated.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Terms & Conditions Acceptance */}
            {((formData.user_type === 'brand_manager' && currentStep === 2) || (formData.user_type === 'vendor' && currentStep === totalSteps)) && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="tc-section">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="tc-accept"
                    checked={tcAccepted}
                    onChange={(e) => setTcAccepted(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    data-testid="tc-checkbox"
                  />
                  <label htmlFor="tc-accept" className="text-sm text-slate-700">
                    I have read and agree to the{' '}
                    {tcTerms ? (
                      <button
                        type="button"
                        onClick={() => setShowTcModal(true)}
                        className="text-blue-600 hover:text-blue-800 underline font-medium"
                        data-testid="tc-view-link"
                      >
                        Terms & Conditions
                      </button>
                    ) : (
                      <span className="text-slate-500">Terms & Conditions</span>
                    )}
                    {' '}<span className="text-red-500">*</span>
                  </label>
                </div>
                {!tcTerms && !tcLoading && (
                  <p className="text-xs text-slate-400 mt-2 ml-7">No T&C currently available. Registration will proceed without T&C acceptance.</p>
                )}
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex gap-4 pt-4 border-t">
              {currentStep > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  className="flex-1"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              )}
              
              {currentStep === 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/marketplace/login')}
                  className="flex-1"
                >
                  Back to Login
                </Button>
              )}
              
              {currentStep < totalSteps ? (
                <Button
                  type="button"
                  onClick={handleNext}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="register-btn"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {formData.user_type === 'vendor' ? 'Uploading Documents...' : 'Registering...'}
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Complete Registration
                    </>
                  )}
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* T&C Modal */}
      <Dialog open={showTcModal} onOpenChange={setShowTcModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              {tcTerms?.title || 'Terms & Conditions'}
              {tcTerms?.version && <Badge variant="outline" className="text-[10px]">v{tcTerms.version}</Badge>}
            </DialogTitle>
          </DialogHeader>
          <div className="prose prose-sm max-w-none whitespace-pre-wrap text-slate-700 bg-slate-50 p-4 rounded-lg border max-h-[55vh] overflow-y-auto" data-testid="tc-modal-content">
            {tcTerms?.content || 'No terms and conditions content available.'}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTcModal(false)}>Close</Button>
            <Button
              onClick={() => { setTcAccepted(true); setShowTcModal(false); }}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="tc-modal-accept-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" /> I Accept
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
